import { computed, ref } from 'vue';
import { QTool } from '@/native/plugins/QTool';
import type {
  QToolFirmwareUpdateErrorReason,
  QToolFirmwareUpdateEvent,
} from '@/native/plugins/QTool';

export type FirmwareUpdatePhase =
  | { phase: 'idle' }
  | { phase: 'available'; currentVersion: string; targetVersion: string }
  | { phase: 'preparing' }
  | { phase: 'updating'; percent: number }
  | { phase: 'complete' }
  | {
      phase: 'error';
      reason: QToolFirmwareUpdateErrorReason;
      detail?: string;
    };

const state = ref<FirmwareUpdatePhase>({ phase: 'idle' });
const dismissing = ref(false);
let listenersInitialized = false;

function applyEvent(event: QToolFirmwareUpdateEvent): void {
  switch (event.state) {
    case 'notRequired':
      state.value = { phase: 'idle' };
      break;
    case 'available':
      state.value = {
        phase: 'available',
        currentVersion: event.currentVersion ?? '',
        targetVersion: event.targetVersion ?? '',
      };
      break;
    case 'preparing':
      state.value = { phase: 'preparing' };
      break;
    case 'progress':
      state.value = { phase: 'updating', percent: event.percent ?? 0 };
      break;
    case 'complete':
      state.value = { phase: 'complete' };
      break;
    case 'error':
      state.value = {
        phase: 'error',
        reason: event.reason ?? 'UNKNOWN',
        detail: event.detail,
      };
      break;
  }
}

function setupListeners() {
  if (listenersInitialized) return;
  listenersInitialized = true;

  QTool.addListener('qtoolFirmwareUpdate', applyEvent);

  QTool.addListener('qtoolModeChanged', (mode) => {
    if (mode.type === 'idle') {
      state.value = { phase: 'idle' };
      dismissing.value = false;
    }
  });
}

export function useQToolFirmwareUpdate() {
  setupListeners();

  const isBlocking = computed(() => {
    const phase = state.value.phase;
    return (
      phase === 'available' ||
      phase === 'preparing' ||
      phase === 'updating' ||
      phase === 'complete' ||
      phase === 'error'
    );
  });

  async function startUpdate() {
    // A second tap before the native `preparing` event has crossed the bridge
    // would send a duplicate start; native drops it while a run is live, but
    // there is no reason to send it at all.
    const phase = state.value.phase;
    if (phase === 'preparing' || phase === 'updating') return;
    state.value = { phase: 'preparing' };
    try {
      await QTool.startFirmwareUpdate();
    } catch (e) {
      // Without this, a rejection (e.g. the native service is unbound) is an
      // unhandled promise from the button handler and the screen never moves.
      state.value = {
        phase: 'error',
        reason: 'UNKNOWN',
        detail: e instanceof Error ? e.message : String(e),
      };
    }
  }

  async function dismiss() {
    const phase = state.value.phase;
    if (phase === 'complete') {
      // Keep the complete screen visible (button spins via `dismissing`)
      // until the native side reports `qtoolModeChanged` `{ type: 'idle' }`,
      // at which point the listener above flips state back to 'idle' and the
      // dialog closes.
      if (dismissing.value) return;
      dismissing.value = true;
      try {
        await QTool.disconnect();
      } catch {
        // ignore
      }
      return;
    }
    if (phase === 'error') {
      state.value = { phase: 'idle' };
    }
  }

  return {
    state,
    isBlocking,
    dismissing,
    startUpdate,
    dismiss,
  };
}
